"use client";

import { useState, useRef, useEffect, useMemo } from "react";
import Image from "next/image";
import { X, ImagePlus, ChevronDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext/AuthContext";
import { getGroupsWhereUserCanPostFromState, useGroupStore } from "@/store/groupStore";
import { useEventStore } from "@/store/eventStore";
import { EventType, Group } from "@/types";
import { useLanguage } from "@/context/LanguageContext";
import { ActivityLogger } from "@/lib/activityLog";
import { getEventCategoryLabelI18n, sortByLabel } from "@/lib/taxonomy-labels";
import {
  getImageUploadCopy,
  MAX_MULTI_IMAGE_UPLOAD_COUNT,
  prepareImageUploads,
} from "@/lib/image-upload";
import { isRecurringEventType } from "@/types";
import { TimePicker } from "@/components/ui/TimePicker";
import {
  ValidationFooter,
  getFieldValidation,
  ERROR_BORDER_CLASS,
  CrisisModal,
  InappropriateLanguageModal,
  InappropriateLanguageBanner,
} from "@/components/ui";
import { useContentModeration } from "@/hooks/useContentModeration";
import { FORM_LIMITS } from "@/lib/formLimits";

interface CreateEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResult?: (success: boolean, message: string) => void;
  onCreated?: (documentId: string) => void;
  preselectedGroupId?: number;
}

const labelClass = "block text-sm font-medium text-foreground mb-1";
const inputClass = "w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary";
const textareaClass = "w-full px-3 py-2 border border-border rounded-md bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary resize-none";
const EMPTY_GROUPS: Group[] = [];
const MAX_IMAGES = MAX_MULTI_IMAGE_UPLOAD_COUNT;
const MAX_CATEGORIES = 4;
const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 5000;
// Stable ids so the hint can be wired to the input via aria-describedby.
const COST_INPUT_ID = "create-event-cost";
const COST_HINT_ID = "create-event-cost-hint";
const COST_REQUIRED_ID = "create-event-cost-required";
const GROUP_REQUIRED_ID = "create-event-group-required";


export default function CreateEventModal({ isOpen, onClose, onResult, onCreated, preselectedGroupId }: CreateEventModalProps) {
  const { t, language } = useLanguage();
  const { currentUser } = useAuth();
  const groups = useGroupStore((state) => state.groups);
  const myMemberships = useGroupStore((state) => state.myMemberships);
  const fetchGroups = useGroupStore((state) => state.fetchGroups);
  const fetchMyMemberships = useGroupStore((state) => state.fetchMyMemberships);
  const groupsLoaded = useGroupStore((state) => state.groupsLoaded);
  const myMembershipsLoaded = useGroupStore((state) => state.myMembershipsLoaded);
  const { createEvent, loading, fetchCategories, categories } = useEventStore();
  const currentUserId = currentUser?.id;

  const userGroups = useMemo(() => {
    if (!currentUserId) {
      return EMPTY_GROUPS;
    }

    return getGroupsWhereUserCanPostFromState(groups, myMemberships, currentUserId);
  }, [currentUserId, groups, myMemberships]);

  // Tracks that we have finished ASKING for the eligible-group list, which is
  // not the same as having got it. The store sets `groupsLoaded` /
  // `myMembershipsLoaded` only on success — both catch blocks leave them false
  // (groupStore.ts, `fetchGroups` and `fetchMyMemberships`) — so a failed
  // request would otherwise pin the modal on "Loading..." forever, with no
  // selector and no way forward (found in review of PR #575).
  //
  // Deliberately NOT fixed by setting those flags in the store's catch blocks:
  // `fetchGroups` early-returns on `if (!force && get().groupsLoaded) return`,
  // so marking a failure as "loaded" would stop every future retry and leave
  // the whole app with an empty group directory.
  const [groupFetchSettled, setGroupFetchSettled] = useState(false);

  // Fetch groups, the current user's memberships, and categories when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // allSettled, not all: a rejection must still count as settled, or the
    // loading state becomes terminal again.
    void Promise.allSettled([
      groups.length === 0 ? fetchGroups() : Promise.resolve(),
      currentUserId ? fetchMyMemberships(currentUserId) : Promise.resolve(),
      fetchCategories(), // needed for slug-to-documentId mapping on submit
    ]).then(() => setGroupFetchSettled(true));
  }, [isOpen, groups.length, fetchGroups, fetchMyMemberships, fetchCategories, currentUserId]);

  // userGroups is empty both while the two fetches are still in flight and when
  // the organizer genuinely runs no groups. Those need different copy: without
  // this flag the modal flashes "you must be an admin or editor" at an organizer
  // who is one, including on the group-tab path where a group is about to be
  // filled in for them (MWBI-929). A signed-out user is never "pending" — there
  // is nothing left to load for them, so mustBeAdmin is the honest answer.
  // `groupFetchSettled` keeps this non-terminal when a request fails: we fall
  // through to mustBeAdmin, which is at least actionable, rather than spinning.
  const eligibleGroupsPending =
    Boolean(currentUserId) && !(groupsLoaded && myMembershipsLoaded) && !groupFetchSettled;

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [timeStart, setTimeStart] = useState("");
  const [timeEnd, setTimeEnd] = useState("");
  const [location, setLocation] = useState("");
  const [cost, setCost] = useState("");
  // Cost is required, and the submit button is disabled rather than clickable,
  // so the modal's usual submitAttempted trigger can never fire — a disabled
  // button dispatches no event. Track first interaction instead, so the reason
  // the button is disabled is stated once the organizer has touched the field
  // (and announced via role="alert"), without scolding an untouched form on open.
  const [costTouched, setCostTouched] = useState(false);
  const [eventType, setEventType] = useState<EventType>("one-off");
  const [bookingLink, setBookingLink] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(preselectedGroupId ?? null);
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState<string[]>([]);
  const [openToPublic, setOpenToPublic] = useState(true);
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [imageNames, setImageNames] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear the chosen group when the modal deactivates (MWBI-929). Both parents
  // render this component unconditionally as <CreateEventModal isOpen={...} />
  // (CreateButton.tsx, EventsTab.tsx), so it stays MOUNTED while closed and its
  // state survives; resetForm() runs only after a successful submit, so Cancel /
  // X / backdrop would otherwise carry a group into the next open and act as the
  // default this ticket removes. Only the group is cleared — the rest of the
  // draft is deliberately kept, so closing by accident does not discard a
  // half-written event.
  //
  // Adjusted during render rather than in an effect: this is React's documented
  // "reset state when a prop changes" pattern. React re-runs this component
  // immediately without committing the discarded render, so there is no extra
  // paint and no cascading-render lint warning. Keying off `isOpen` rather than
  // wrapping onClose keeps it correct even for a parent that lowers `isOpen`
  // without routing through our close handlers.
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen);
    if (!isOpen) {
      setSelectedGroupId(null);
    }
  }

  // No default group (MWBI-929). Nothing is pre-selected, so the organizer picks
  // deliberately — exactly what CreateNewsModal already requires for a new post.
  // isSubmitDisabled reads this, so Create Event stays disabled until a group is
  // chosen. Do NOT reintroduce a `?? userGroups[0]?.id` fallback: it silently
  // published events under whichever group happened to sort first.
  const effectiveSelectedGroupId = preselectedGroupId ?? selectedGroupId ?? null;
  const isRecurringEvent = isRecurringEventType(eventType);
  const uploadCopy = getImageUploadCopy(language, 'multiple');

  // Content moderation (MWBI-869). Scan the human-text fields only — title +
  // description + location + cost — joined with "\n". Cost joined the scan in
  // MWBI-919, when it stopped being a number and became free text the organizer
  // writes themselves. The URL field (bookingLink) is
  // intentionally NOT scanned. The "\n" join stops a banned word from forming
  // across a field boundary: the inappropriate detector's \b…\b regex treats
  // "\n" as a non-word char, so it can't match across the seam. The crisis
  // detector uses a plain substring includes() (see utils/crisis.ts), so the
  // join only shields it insofar as no crisis keyword itself contains a "\n" —
  // which holds for every default keyword. Called above the isOpen
  // early-return to preserve the Rules of Hooks; the scanned text is gated on
  // isOpen so detection stays inert while the modal is mounted-but-closed
  // (parents keep this component mounted via the isOpen prop).
  const moderationText = isOpen ? [title, description, location, cost].join('\n') : '';
  const {
    hasInappropriate,
    showCrisisModal,
    showInappropriateModal,
    dismissCrisisModal,
    dismissInappropriateModal,
  } = useContentModeration(moderationText);

  if (!isOpen) return null;

  const handleFiles = async (files: FileList | File[]) => {
    const allImageFiles = Array.from(files).filter((file) => file.type.startsWith("image/"));
    const availableSlots = Math.max(0, MAX_IMAGES - images.length);
    const filesToAdd = allImageFiles.slice(0, availableSlots);
    if (filesToAdd.length === 0) return;

    const { prepared, error } = await prepareImageUploads(filesToAdd, language);
    if (error) {
      onResult?.(false, error);
      return;
    }

    setImages((currentImages) => [...currentImages, ...prepared]);
    setPreviews((currentPreviews) => [
      ...currentPreviews,
      ...prepared.map((file) => URL.createObjectURL(file)),
    ]);
    setImageNames((currentNames) => [...currentNames, ...prepared.map((file) => file.name)]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      void handleFiles(e.dataTransfer.files);
    }
  };

const removeImage = (index: number) => {
    const previewToRemove = previews[index];
    if (previewToRemove) {
      URL.revokeObjectURL(previewToRemove);
    }

    setImages((currentImages) => currentImages.filter((_, currentIndex) => currentIndex !== index));
    setPreviews((currentPreviews) => currentPreviews.filter((_, currentIndex) => currentIndex !== index));
    setImageNames((currentNames) => currentNames.filter((_, currentIndex) => currentIndex !== index));
  };

  const handleCategoryToggle = (slug: string) => {
    setSelectedCategorySlugs((currentSlugs) => {
      if (currentSlugs.includes(slug)) {
        return currentSlugs.filter((currentSlug) => currentSlug !== slug);
      }

      if (currentSlugs.length >= MAX_CATEGORIES) {
        return currentSlugs;
      }

      return [...currentSlugs, slug];
    });
  };

  const resetForm = () => {
    previews.forEach((previewUrl) => URL.revokeObjectURL(previewUrl));
    setTitle("");
    setDescription("");
    setDate("");
    setEndDate("");
    setTimeStart("");
    setTimeEnd("");
    setLocation("");
    setCost("");
    setCostTouched(false);
    setEventType("one-off");
    setBookingLink("");
    setSelectedGroupId(null);
    setSelectedCategorySlugs([]);
    setOpenToPublic(true);
    setImages([]);
    setPreviews([]);
    setImageNames([]);
  };

  const titleValidation = getFieldValidation({
    value: title,
    maxLength: MAX_TITLE_LENGTH,
    tooLongMessage: t.errors.eventTitleTooLong,
  });
  const descriptionValidation = getFieldValidation({
    value: description,
    maxLength: MAX_DESCRIPTION_LENGTH,
    tooLongMessage: t.errors.eventDescriptionTooLong,
  });

  // Drives both the inline reason and the submit button's aria-describedby, so
  // the explanation a sighted user sees and the one a screen reader gets are
  // the same string from the same condition.
  const showCostRequired = costTouched && !cost.trim();

  // Why the submit button is disabled, stated for everyone rather than implied
  // by a greyed-out button. Suppressed while the eligible-group list is still
  // loading, and on the mustBeAdmin branch, where picking a group is not
  // something the organizer can act on.
  const showGroupRequired = !effectiveSelectedGroupId && !eligibleGroupsPending && userGroups.length > 0;

  // aria-describedby takes a space-separated id list, so both reasons can be
  // announced together when both apply. undefined (not "") when neither does —
  // an empty attribute would point at nothing.
  const submitDescribedBy = [
    showGroupRequired ? GROUP_REQUIRED_ID : null,
    showCostRequired ? COST_REQUIRED_ID : null,
  ].filter(Boolean).join(' ') || undefined;

  // Resolved during render, not inside handleSubmit, because the submit gate
  // below depends on it. `createEvent` needs the group's documentId, which only
  // exists once the group directory has arrived — and on the preselectedGroupId
  // path `effectiveSelectedGroupId` is truthy IMMEDIATELY, before `groups` has
  // loaded. The group-detail page keeps its group in local state and never calls
  // fetchGroups, so arriving via a deep link to /groups/<id>?tab=events leaves
  // `groups` empty; the organizer could then fill the form faster than the
  // directory query returns, press an enabled button and have nothing happen
  // (found in review of PR #575). Gating on this makes the button honestly
  // disabled instead of dead.
  const groupForSubmit = groups.find((group) => group.id === effectiveSelectedGroupId);

  const isSubmitDisabled =
    loading ||
    !title.trim() ||
    !date ||
    !location.trim() ||
    !effectiveSelectedGroupId ||
    !groupForSubmit ||
    !timeStart ||
    !cost.trim() ||
    !description.trim() ||
    titleValidation.isOver ||
    descriptionValidation.isOver ||
    hasInappropriate;

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    // Validate that the event date is not in the past
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    const eventDate = new Date(date);

    if (eventDate < today) {
      onResult?.(false, t.modals.event.pastDateError);
      return;
    }

    if (isRecurringEvent) {
      if (!endDate) {
        onResult?.(false, t.modals.event.recurrenceEndRequired);
        return;
      }

      const recurrenceEndDate = new Date(endDate);
      recurrenceEndDate.setHours(0, 0, 0, 0);

      if (recurrenceEndDate < eventDate) {
        onResult?.(false, t.modals.event.recurrenceEndAfterStart);
        return;
      }
    }

    // Backstop only — `isSubmitDisabled` already requires `groupForSubmit`, so a
    // real user cannot press the button in this state. Kept because a
    // function-level early return is the last line of defence, but it must SAY
    // something rather than be a silent no-op: a dead click with no feedback is
    // indistinguishable from a broken app.
    if (!groupForSubmit) {
      onResult?.(false, t.errors.somethingWentWrong);
      return;
    }

    // Close modal immediately
    resetForm();
    onClose();

    try {
      const result = await createEvent({
        title: title.trim(),
        description: description.trim() || undefined,
        date,
        endDate: isRecurringEvent ? endDate : null,
        timeStart: timeStart || undefined,
        timeEnd: timeEnd || undefined,
        location: location.trim(),
        cost: cost.trim(),
        eventType,
        bookingLink: bookingLink.trim() || undefined,
        groupDocumentId: groupForSubmit.documentId,
        categorySlugs: selectedCategorySlugs,
        organizerName: currentUser?.displayName || currentUser?.username || "Unknown",
        openToPublic,
        image: images[0] ?? null,
        images: images.length > 0 ? images : undefined,
      });

      if (result.success) {
        onResult?.(true, t.modals.event.eventCreated);
        if (currentUser) {
          ActivityLogger.createEvent(currentUser, title.trim());
        }
        if (result.eventDocumentId) {
          onCreated?.(result.eventDocumentId);
        }
      } else {
        onResult?.(false, result.error || t.modals.event.failedCreate);
      }
    } catch (error) {
      console.error("Error creating event:", error);
      onResult?.(false, t.modals.event.errorCreating);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-event-root">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-4 border-b border-border sticky top-0 bg-background z-10">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-foreground">{t.modals.event.createEvent}</h2>
            <button onClick={onClose} data-testid="create-event-close-btn" className="p-1 hover:bg-sidebar-hover rounded transition">
              <X size={20} className="text-foreground-muted" />
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* User avatar */}
            <div
              className="w-10 h-10 rounded-full overflow-hidden shrink-0 flex items-center justify-center"
              style={{ backgroundColor: currentUser?.avatarColor || '#00423E' }}
            >
              {currentUser?.avatarUrl ? (
                <Image
                  src={currentUser.avatarUrl}
                  alt={currentUser.displayName}
                  width={40}
                  height={40}
                  className="object-cover w-full h-full"
                />
              ) : (
                <span className="text-white font-medium text-sm">
                  {currentUser?.initials || currentUser?.displayName?.substring(0, 2).toUpperCase()}
                </span>
              )}
            </div>

            <span className="font-medium text-foreground text-sm">
              {currentUser?.displayName || currentUser?.username}
            </span>

            {/* Group selector. Three states, deliberately distinct: still
                loading, has eligible groups, has none. */}
            {eligibleGroupsPending ? (
              <p className="ml-auto text-xs text-caption" data-testid="create-event-group-loading">
                {t.common.loading}
              </p>
            ) : userGroups.length > 0 ? (
              <div className="ml-auto flex flex-col items-end gap-1">
                <div className="relative">
                  <select
                    value={effectiveSelectedGroupId ?? ""}
                    // `|| null` is correct here and must stay: Number("") is 0, and
                    // `?? null` would let that 0 through as a group id.
                    onChange={(e) => setSelectedGroupId(Number(e.target.value) || null)}
                    disabled={!!preselectedGroupId}
                    aria-label={t.modals.event.postingAsGroup}
                    aria-describedby={showGroupRequired ? GROUP_REQUIRED_ID : undefined}
                    data-testid="create-event-group-select"
                    className={`appearance-none pl-3 pr-8 py-1.5 border border-border rounded-full bg-background-secondary text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary ${preselectedGroupId ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`}
                  >
                    {/* Disabled so the prompt can never be submitted as a value. */}
                    <option value="" disabled>{t.modals.event.selectGroup}</option>
                    {userGroups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-muted pointer-events-none" />
                </div>

                {showGroupRequired && (
                  <p
                    id={GROUP_REQUIRED_ID}
                    role="status"
                    data-testid="create-event-group-required"
                    className="text-xs text-caption"
                  >
                    {t.modals.event.selectGroupRequired}
                  </p>
                )}
              </div>
            ) : (
              <p className="ml-auto text-xs text-caption">{t.modals.event.mustBeAdmin}</p>
            )}
          </div>
        </div>

        <div className="p-4 space-y-4">
          <CrisisModal
            open={showCrisisModal}
            onClose={dismissCrisisModal}
            testIdPrefix="create-event-crisis"
          />

          <InappropriateLanguageModal
            open={showInappropriateModal}
            onClose={dismissInappropriateModal}
            testIdPrefix="create-event-inappropriate"
          />

          {hasInappropriate && (
            <InappropriateLanguageBanner testIdPrefix="create-event-inappropriate" />
          )}

          <div>
            <label className={labelClass}>{t.modals.event.eventName} <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder={t.modals.event.addEventName}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="create-event-title-input"
              className={`${inputClass} ${titleValidation.isOver ? ERROR_BORDER_CLASS : ''}`}
            />
            <ValidationFooter
              value={title}
              maxLength={MAX_TITLE_LENGTH}
              tooLongMessage={t.errors.eventTitleTooLong}
              testIdPrefix="create-event-title"
            />
          </div>

          {effectiveSelectedGroupId && (
            <div>
              <label className={labelClass}>{t.modals.event.eventAccess}</label>
              <div className="rounded-lg border border-border bg-background-secondary p-1">
                <div className="grid grid-cols-2 gap-1">
                  <button
                    type="button"
                    aria-pressed={openToPublic}
                    onClick={() => setOpenToPublic(true)}
                    data-testid="create-event-open-to-public-btn"
                    className={`rounded-md px-4 py-2.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                      openToPublic
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    {t.modals.event.openToPublic}
                  </button>
                  <button
                    type="button"
                    aria-pressed={!openToPublic}
                    onClick={() => setOpenToPublic(false)}
                    data-testid="create-event-members-only-btn"
                    className={`rounded-md px-4 py-2.5 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                      !openToPublic
                        ? 'bg-background text-foreground shadow-sm'
                        : 'text-foreground-muted hover:text-foreground'
                    }`}
                  >
                    {t.modals.event.membersOnly}
                  </button>
                </div>
              </div>
              <p className="mt-2 text-xs text-caption">
                {openToPublic ? t.modals.event.anyoneCanRsvp : t.modals.event.onlyMembersRsvp}
              </p>
            </div>
          )}

          <div>
            <label className={labelClass}>{t.modals.event.eventPhoto} ({previews.length}/{MAX_IMAGES})</label>
            {previews.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                {previews.map((preview, index) => (
                  <div key={preview} className="flex flex-col gap-1">
                    <div className="relative aspect-square rounded-md overflow-hidden border border-border bg-background-secondary">
                      <Image src={preview} alt={`Preview ${index + 1}`} fill className="object-contain" unoptimized />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        data-testid={`create-event-remove-image-btn-${index}`}
                        className="absolute top-2 right-2 rounded-full bg-red-500 p-1 text-white hover:bg-red-600 transition"
                        aria-label={`Remove image ${index + 1}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                    {imageNames[index] && <p className="text-xs text-caption truncate">{imageNames[index]}</p>}
                  </div>
                ))}
              </div>
            )}
            <div
              className="flex flex-col items-center"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              <button
                type="button"
                onClick={() => { if (images.length < MAX_IMAGES) fileInputRef.current?.click(); }}
                disabled={images.length >= MAX_IMAGES}
                data-testid="create-event-drop-zone-btn"
                className="w-32 h-32 rounded-md border border-dashed border-border bg-background-secondary flex flex-col items-center justify-center gap-1.5 hover:bg-sidebar-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ImagePlus size={28} className="text-foreground-muted" />
                <span className="text-sm text-foreground-muted">{uploadCopy.uploadPrompt}</span>
              </button>
              <p className="text-xs text-caption mt-1.5 text-center">{uploadCopy.fileTypes}</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                multiple
                data-testid="create-event-file-input"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files) {
                    void handleFiles(e.target.files);
                  }
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="create-event-date-type-row">
            <div>
              <label className={labelClass}>{t.modals.event.date} <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                data-testid="create-event-start-date-input"
                className={inputClass}
              />
            </div>
            <div>
              <label className={labelClass}>{t.modals.event.eventType}</label>
              <select
                value={eventType}
                onChange={(e) => setEventType(e.target.value as EventType)}
                data-testid="create-event-event-type-select"
                className={inputClass}
              >
                <option value="one-off">{t.modals.event.oneOff}</option>
                <option value="recurring-daily">{t.modals.event.recurringDaily}</option>
                <option value="recurring-weekly">{t.modals.event.recurringWeekly}</option>
                <option value="recurring-bi-weekly">{t.modals.event.recurringBiWeekly}</option>
                <option value="recurring-monthly">{t.modals.event.recurringMonthly}</option>
              </select>
            </div>
          </div>

          {isRecurringEvent && (
            <div>
              <label className={labelClass}>{t.modals.event.endDate} <span className="text-red-500">*</span></label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={date || new Date().toISOString().split('T')[0]}
                data-testid="create-event-end-date-input"
                className={inputClass}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="create-event-time-row">
            <div>
              <label className={labelClass}>{t.modals.event.startTime} <span className="text-red-500">*</span></label>
              <TimePicker value={timeStart} onChange={setTimeStart} testIdPrefix="create-event-start" />
            </div>
            <div>
              <label className={labelClass}>{t.modals.event.endTime}</label>
              <TimePicker value={timeEnd} onChange={setTimeEnd} testIdPrefix="create-event-end" />
            </div>
          </div>

          {/* Cost — free text, shown to members exactly as typed (MWBI-919).
              Mirrors the Provider cost field, the locked reference for this
              field family: short label, hint paragraph, one open input. */}
          <div>
            <label className={labelClass} htmlFor={COST_INPUT_ID}>
              {t.modals.event.cost} <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-caption -mt-1 mb-1.5" id={COST_HINT_ID}>
              {t.modals.event.costHint}
            </p>
            <input
              id={COST_INPUT_ID}
              type="text"
              placeholder={t.modals.event.costPlaceholder}
              value={cost}
              onChange={(e) => {
                setCostTouched(true);
                setCost(e.target.value);
              }}
              onBlur={() => setCostTouched(true)}
              maxLength={FORM_LIMITS.eventCost.max}
              aria-describedby={showCostRequired ? `${COST_HINT_ID} ${COST_REQUIRED_ID}` : COST_HINT_ID}
              data-testid="create-event-cost-input"
              className={`${inputClass} ${showCostRequired ? ERROR_BORDER_CLASS : ''}`}
            />
            {/* Counter only, no over-limit message: maxLength truncates typing
                AND paste, so an "over the limit" state is unreachable and a
                test for it could never fail. */}
            <ValidationFooter
              value={cost}
              maxLength={FORM_LIMITS.eventCost.max}
              testIdPrefix="create-event-cost"
            />
            {showCostRequired && (
              <p
                id={COST_REQUIRED_ID}
                role="alert"
                data-testid="create-event-cost-required"
                className="text-xs text-red-500 font-medium mt-1"
              >
                {t.modals.event.costRequired}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>{t.modals.event.location} <span className="text-red-500">*</span></label>
            <input
              type="text"
              placeholder={t.modals.event.enterLocation}
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="create-event-location-input"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.modals.event.bookingLink}</label>
            <input
              type="url"
              placeholder={t.modals.event.bookingLinkPlaceholder}
              value={bookingLink}
              onChange={(e) => setBookingLink(e.target.value)}
              data-testid="create-event-booking-link-input"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>{t.modals.event.addDetails} <span className="text-red-500">*</span></label>
            <textarea
              placeholder={t.modals.event.describeEvent}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              data-testid="create-event-description-textarea"
              className={`${textareaClass} ${descriptionValidation.isOver ? ERROR_BORDER_CLASS : ''}`}
            />
            <ValidationFooter
              value={description}
              maxLength={MAX_DESCRIPTION_LENGTH}
              tooLongMessage={t.errors.eventDescriptionTooLong}
              testIdPrefix="create-event-description"
            />
          </div>

          <div>
            <label className={labelClass}>
              {t.modals.event.categoriesOptional} ({t.modals.event.selectUpToFour})
            </label>
            <div className="border border-border rounded-md p-3 max-h-48 overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {sortByLabel(
                  categories.filter((category) => !category.archived),
                  (category) => getEventCategoryLabelI18n(t, category.slug, category.name),
                  language,
                )
                  .map((category) => {
                    const isSelected = selectedCategorySlugs.includes(category.slug);
                    const isDisabled = !isSelected && selectedCategorySlugs.length >= MAX_CATEGORIES;

                    return (
                      <label
                        key={category.slug}
                        className="flex items-center gap-2 cursor-pointer hover:bg-sidebar-hover p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleCategoryToggle(category.slug)}
                          disabled={isDisabled}
                          data-testid={`create-event-category-${category.slug}`}
                          className="w-4 h-4 rounded border-border"
                        />
                        <span className="text-sm text-foreground">{getEventCategoryLabelI18n(t, category.slug, category.name)}</span>
                      </label>
                    );
                  })}
              </div>
            </div>
            <p className="text-xs text-caption mt-1">
              {t.modals.event.categoriesSelected.replace('{count}', String(selectedCategorySlugs.length))}
            </p>
          </div>

        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-border sticky bottom-0 bg-background">
          <button
            onClick={onClose}
            data-testid="create-event-cancel-btn"
            className="px-4 py-2 text-sm font-medium text-foreground hover:bg-sidebar-hover rounded-md transition"
          >
            {t.common.cancel}
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            aria-describedby={submitDescribedBy}
            data-testid="create-event-submit-btn"
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t.modals.event.creating : t.modals.event.addEvent}
          </button>
        </div>
      </div>
    </div>
  );
}
